import { type FormEvent, useEffect, useState } from "react";
import { isIsoDate } from "../../shared/time";
import type { BusinessSettings } from "../../shared/types";
import { ErrorBanner, Field, Loading } from "../components/ui";
import { put } from "../lib/api";
import { useSession } from "../session";
import { Check, changedFields, PageHeader, RadioCards, toApiError, useSettings } from "./common";

const ROUNDING = [1, 5, 6, 10, 15] as const;

/** Numeric fields with their allowed ranges (mirrors the API's validation). */
const LIMITS = {
  defaultRadiusM: [20, 2000, "Default radius"],
  autoFlagHours: [1, 48, "Missing clock-out after"],
  gpsRetentionDays: [7, 3650, "Keep exact GPS for"],
  recordRetentionYears: [1, 20, "Keep records for"],
  lateGraceMinutes: [0, 240, "Late after"],
} as const;

function NumberInput({
  value,
  onChange,
  min,
  max,
  unit,
}: {
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  unit: string;
}) {
  return (
    <div className="row" style={{ flexWrap: "nowrap" }}>
      <input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={Number.isFinite(value) ? value : ""}
        onChange={(e) => onChange(e.target.value === "" ? Number.NaN : Number(e.target.value))}
        style={{ maxWidth: 140 }}
      />
      <span className="muted">{unit}</span>
    </div>
  );
}

function SettingsForm({
  initial,
  onSaved,
}: {
  initial: BusinessSettings;
  onSaved: (s: BusinessSettings) => void;
}) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: "ok" | "danger"; text: string } | null>(null);
  const set = <K extends keyof BusinessSettings>(k: K, v: BusinessSettings[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => setForm(initial), [initial]);
  const changes = changedFields(initial, form);
  const dirty = Object.keys(changes).length > 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setStatus(null);
    for (const [k, [min, max, label]] of Object.entries(LIMITS)) {
      const v = form[k as keyof typeof LIMITS];
      if (!Number.isInteger(v) || v < min || v > max)
        return setStatus({ kind: "danger", text: `${label} must be a whole number from ${min} to ${max}.` });
    }
    if (!form.businessName.trim()) return setStatus({ kind: "danger", text: "Enter the business name." });
    if (!isIsoDate(form.payPeriodAnchor))
      return setStatus({ kind: "danger", text: "Choose the date pay periods start on." });
    if (!form.exportServiceName.trim())
      return setStatus({ kind: "danger", text: "Enter the QuickBooks service name." });
    setBusy(true);
    try {
      onSaved(await put<BusinessSettings>("/admin/settings", changes));
      setStatus({ kind: "ok", text: "Settings saved." });
    } catch (err) {
      setStatus({ kind: "danger", text: toApiError(err).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={submit}>
      <section className="card stack">
        <h2>Business</h2>
        <Field label="Business name" hint="Shown at the top of the app and printed on QR stickers.">
          <input
            value={form.businessName}
            onChange={(e) => set("businessName", e.target.value)}
            maxLength={120}
            required
          />
        </Field>
      </section>

      <section className="card stack">
        <h2>Clocking in and out</h2>
        <RadioCards
          legend="If someone clocks in or out from outside the site's geofence"
          hint="Each site can override this on its own page."
          name="fenceMode"
          value={form.fenceMode}
          onChange={(v) => set("fenceMode", v)}
          columns
          options={[
            {
              value: "block",
              title: "Block",
              description:
                "The clock-in or out is stopped. The cleaner can tap “Request approval”; it then waits on Today, and if you approve it their original time stands.",
            },
            {
              value: "flag",
              title: "Flag",
              description:
                "It's accepted straight away but marked “Outside geofence” (with the distance) so you can check it on Today and in Timesheets.",
            },
          ]}
        />
        <RadioCards
          legend="If the phone can't give a good location"
          hint="For example location is turned off, the signal is poor indoors, or the phone only shares an approximate location."
          name="locationUnavailableMode"
          value={form.locationUnavailableMode}
          onChange={(v) => set("locationUnavailableMode", v)}
          columns
          options={[
            {
              value: "approval",
              title: "Needs approval",
              description:
                "The time is recorded but waits for your approval on Today before it counts. Safest with Block.",
            },
            {
              value: "flag",
              title: "Flag",
              description: "It's accepted straight away but marked “Location unavailable” for you to check.",
            },
          ]}
        />
        <div className="grid cols-2">
          <Field
            label="Default radius for new sites"
            hint="How close to the site pin a phone must be. 100 m suits most buildings."
          >
            <NumberInput
              value={form.defaultRadiusM}
              onChange={(n) => set("defaultRadiusM", n)}
              min={20}
              max={2000}
              unit="metres"
            />
          </Field>
          <Field
            label="Mark as missing clock-out after"
            hint="A visit still open this long is flagged for you to fix. It's never closed automatically."
          >
            <NumberInput
              value={form.autoFlagHours}
              onChange={(n) => set("autoFlagHours", n)}
              min={1}
              max={48}
              unit="hours"
            />
          </Field>
          <Field
            label="Show as late after"
            hint="How long after a scheduled start time someone who hasn't clocked in is shown as late."
          >
            <NumberInput
              value={form.lateGraceMinutes}
              onChange={(n) => set("lateGraceMinutes", n)}
              min={0}
              max={240}
              unit="minutes"
            />
          </Field>
        </div>
      </section>

      <section className="card stack">
        <h2>Pay and exports</h2>
        <div className="grid cols-2">
          <Field label="Pay period" hint="Used for the period choices on the Exports page.">
            <select
              value={form.payPeriod}
              onChange={(e) => set("payPeriod", e.target.value as BusinessSettings["payPeriod"])}
            >
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="monthly">Monthly (calendar month)</option>
            </select>
          </Field>
          <Field
            label="Pay periods start on"
            hint={
              form.payPeriod === "monthly"
                ? "Not used for monthly pay; periods run from the 1st to the end of the month."
                : "Any date a pay period started, e.g. a Monday. Later periods follow on from it."
            }
          >
            <input
              type="date"
              value={form.payPeriodAnchor}
              onChange={(e) => set("payPeriodAnchor", e.target.value)}
              disabled={form.payPeriod === "monthly"}
              required
            />
          </Field>
          <Field
            label="Round hours in exports to the nearest"
            hint="Only exports are rounded. The exact clock-in and out times are always kept."
          >
            <select
              value={form.roundingMinutes}
              onChange={(e) => set("roundingMinutes", Number(e.target.value))}
            >
              {ROUNDING.map((r) => (
                <option key={r} value={r}>
                  {r === 1 ? "minute (no rounding)" : `${r} minutes`}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="QuickBooks service name"
            hint="The product or service put on every timesheet row, and on time sent straight to QuickBooks. Must match QuickBooks exactly, e.g. Cleaning."
          >
            <input
              value={form.exportServiceName}
              onChange={(e) => set("exportServiceName", e.target.value)}
              maxLength={100}
              required
            />
          </Field>
        </div>
        <RadioCards
          legend="Timesheet detail shows"
          name="exportTimeFormat"
          value={form.exportTimeFormat}
          onChange={(v) => set("exportTimeFormat", v)}
          columns
          options={[
            {
              value: "start_end",
              title: "Start and end times",
              description: "Each row has the clock-in and clock-out time, plus the break.",
            },
            {
              value: "duration",
              title: "Hours and minutes only",
              description: "Each row just has how long was worked. Use this if your importer rejects times.",
            },
          ]}
        />
        <Check
          label="Mark exported time as billable to the customer (CSV files only: time sent straight to QuickBooks is always Not billable)"
          checked={form.exportBillable}
          onChange={(v) => set("exportBillable", v)}
        />
      </section>

      <section className="card stack">
        <h2>Privacy and record keeping</h2>
        <div className="grid cols-2">
          <Field
            label="Keep exact GPS positions for"
            hint="After this, the position is deleted. Whether they were inside the fence, and how far away, is kept."
          >
            <NumberInput
              value={form.gpsRetentionDays}
              onChange={(n) => set("gpsRetentionDays", n)}
              min={7}
              max={3650}
              unit="days"
            />
          </Field>
          <Field
            label="Keep visit and hours records for"
            hint="Pay records are normally kept for 6 years. Older visits are deleted after this."
          >
            <NumberInput
              value={form.recordRetentionYears}
              onChange={(n) => set("recordRetentionYears", n)}
              min={1}
              max={20}
              unit="years"
            />
          </Field>
        </div>
      </section>

      <div className="adm-form-actions">
        <button type="submit" className="btn primary" disabled={busy || !dirty}>
          {busy ? "Saving…" : "Save settings"}
        </button>
        {dirty && !busy && (
          <button type="button" className="btn" onClick={() => setForm(initial)}>
            Undo changes
          </button>
        )}
        {status && (
          <span
            className={`banner ${status.kind} small`}
            role={status.kind === "danger" ? "alert" : "status"}
          >
            {status.text}
          </span>
        )}
      </div>
    </form>
  );
}

export function AdminSettingsPage() {
  const settings = useSettings();
  const session = useSession();
  return (
    <div className="page">
      <PageHeader
        title="Settings"
        sub="These apply to the whole business. Changes are kept in the audit log."
      />
      {settings.data ? (
        <SettingsForm
          initial={settings.data}
          onSaved={(s) => {
            settings.setData(s);
            // The business name is shown in the header, which comes from the session.
            if (s.businessName !== session.me?.settings.businessName) void session.reload();
          }}
        />
      ) : settings.error ? (
        <ErrorBanner error={settings.error} onRetry={settings.reload} />
      ) : (
        <Loading />
      )}
    </div>
  );
}
